import React, { useEffect, useState } from 'react'
import { BREAKPOINT, ThemeContextValue, uiPrimitives, useIsMobile, useThemedStyles } from '../theme'
import { Notice, fetchPublishedNotices, hasSeenNotice, markNoticeSeen } from './admin'

/**
 * 首页通知弹窗。
 *
 * 一次只弹一条，按 `sort` 顺序逐条推进；关掉一条才出现下一条。
 * 一次全弹会把首页糊住 —— 而通知通常就是要一条一条读的。
 *
 * 已读状态记在 localStorage（键含 updated_at，管理员改过就重新弹）。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	backdrop: {
		position: 'fixed' as const,
		// 不用 `inset: 0` 简写：UC/微信等内核（Chromium < 87）不认，
		// 忽略后 fixed 元素零尺寸，整个弹窗塌在原地。逐条写偏移。
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		background: 'rgba(0,0,0,.42)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 2000,
		padding: 16,
	},
	box: {
		background: t.palette.paper2,
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.lg,
		boxShadow: t.shadows.lg,
		padding: 24,
		width: 460,
		maxWidth: '100%',
		maxHeight: '80vh',
		overflowY: 'auto',
		color: t.palette.ink,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			padding: 18,
		},
	},
	title: {
		fontFamily: t.fonts.serif,
		fontSize: 18,
		fontWeight: 700,
		margin: '0 0 10px',
	},
	body: {
		fontFamily: t.fonts.serif,
		fontSize: 14,
		lineHeight: 1.75,
		color: t.palette.ink2,
		// 正文是纯文本不是 HTML，管理员敲的换行要真的换行
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		margin: '0 0 18px',
	},
	acts: {
		display: 'flex',
		gap: 8,
		justifyContent: 'flex-end',
		alignItems: 'center',
		flexWrap: 'wrap',
	},
	more: {
		fontSize: 12,
		color: t.palette.ink3,
		marginRight: 'auto',
	},
})

export function NoticeModal() {
	const classes = useThemedStyles(stylesFactory)
	const isMobile = useIsMobile()
	const [queue, setQueue] = useState<Notice[] | null>(null)

	useEffect(() => {
		let cancelled = false
		fetchPublishedNotices().then(notices => {
			if(cancelled) return
			// 过滤掉已读的那些；全读过了就保持空数组（不渲染弹窗）
			setQueue(notices.filter(n => !hasSeenNotice(n)))
		})
		return () => {
			cancelled = true
		}
	}, [])

	const current = queue && queue.length > 0 ? queue[0] : null

	function closeCurrent() {
		if(!current) return
		markNoticeSeen(current)
		setQueue(q => (q ? q.slice(1) : q))
	}

	// Esc 关闭当前这条。按 document 监听，而不是给 box 加 onKeyDown ——
	// 弹窗打开时焦点还在 body 上，键盘事件压根不会走到那个 div。
	useEffect(() => {
		if(!current) return
		const onKey = (e: KeyboardEvent) => {
			if(e.key === 'Escape') closeCurrent()
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [current])

	if(!current) return null

	function followLink() {
		markNoticeSeen(current!)
		setQueue(q => (q ? q.slice(1) : q))
		window.open(current!.link!, '_blank', 'noopener')
	}

	return <div className={classes.backdrop}>
		<div className={classes.box} role="dialog" aria-modal="true" aria-label={current.title}>
			<h3 className={classes.title}>{current.title}</h3>
			{current.body && <p className={classes.body}>{current.body}</p>}
			<div className={classes.acts}>
				{/* 队列里还有下一条时，措辞用「下一条」，让用户知道不是最后一条 */}
				{queue!.length > 1 && <span className={classes.more}>还有 {queue!.length - 1} 条通知</span>}
				<button
					className={classes.tb + (queue!.length > 1 ? ' ' + classes.tbOutline : ' ' + classes.tbPrimary)}
					onClick={closeCurrent}
				>
					{queue!.length > 1 ? '下一条' : '知道了'}
				</button>
				{current.link && <button className={classes.tb} onClick={followLink}>
					{current.link_label || '查看详情'}
				</button>}
			</div>
		</div>
	</div>
}